import axios, { AxiosError, type AxiosInstance } from 'axios';
import { auth } from './firebase';

/**
 * Single Axios instance for the admin webapp. Two interceptors:
 *
 *   1. **Auth** — pulls a Firebase ID token before every request and
 *      sets `Authorization: Bearer …`. Tokens are cached by the
 *      Firebase SDK; `getIdToken(false)` is cheap.
 *   2. **Error** — turns NestJS error responses into a plain
 *      `ApiError` so callers can show a friendly message without
 *      poking at Axios internals.
 *
 * Base URL behaviour:
 *   - `dev` → relative `/api/v1` so Vite's proxy (vite.config.ts)
 *     forwards to the local NestJS server on :3000.
 *   - `prod` → relative `/api/v1` also; Firebase Hosting rewrites
 *     `/api/**` to the deployed Cloud Function. Same origin, no CORS.
 */
const baseURL = '/api/v1';

export class ApiError extends Error {
  readonly statusCode?: number;
  constructor(message: string, statusCode?: number) {
    super(message);
    this.statusCode = statusCode;
    this.name = 'ApiError';
  }
}

function buildClient(): AxiosInstance {
  const client = axios.create({
    baseURL,
    timeout: 12_000,
    validateStatus: (s) => s < 500,
  });

  client.interceptors.request.use(async (config) => {
    const user = auth.currentUser;
    if (user) {
      try {
        const token = await user.getIdToken();
        config.headers.set('Authorization', `Bearer ${token}`);
      } catch (e) {
        console.warn('[api] could not get ID token', e);
      }
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => {
      const status = response.status;
      if (status >= 400) {
        throw toApiError(response.data, status);
      }
      return response;
    },
    (error: AxiosError) => {
      const status = error.response?.status;
      throw toApiError(error.response?.data, status, error.message);
    },
  );

  return client;
}

function toApiError(
  data: unknown,
  status?: number,
  fallback?: string,
): ApiError {
  if (data && typeof data === 'object' && 'message' in data) {
    const m = (data as { message: unknown }).message;
    const text = Array.isArray(m) ? m.join(', ') : String(m);
    return new ApiError(text, status);
  }
  return new ApiError(
    fallback ?? 'Network error. Check your connection.',
    status,
  );
}

export const api = buildClient();
